import { createFileRoute } from "@tanstack/react-router";
import { createFormData, useSimpleForm } from "@/hooks/use-simple-form";
import useAppStore from "@/store";
import { useTranslate } from "@/hooks/use-translate";
import { Button } from "@/components/ui/button";
import { ProductForm } from "@/components/product/product-form";
import { useEffect, useState } from "react";
import type { Product } from "@/store/Product";
import { Alert } from "@/components/ui/alert";
import { pick, unPick } from "@/lib/utils";
import type { ImageFile } from "@/components/ui/form";
import type { Asset } from "@/store/Asset";
import BackButton from "@/components/ui/shared/back-button";

export const Route = createFileRoute("/(dasboard)/products/products/$id")({
  component: EditProductPage,
});

function EditProductPage() {
  const store = useAppStore();
  const { t } = useTranslate();
  const params = Route.useParams();
  const product = store.product.current as Product;

  const form = useSimpleForm({
    images: [],
    featured_image: undefined,
  });
  const [currentImages, setCurrentImages] = useState<ImageFile[]>([]);

  useEffect(() => {
    const id = params.id as string;
    if (id) {
      store.product
        .fetchOne(id)
        .then((product) => {
          store.product.setCurrent(product);
          initData(product);
        })
        .catch((error: any) => {
          store.errors.catch(error);
        });
    }
  }, [params.id]);

  useEffect(() => {
    store.category.fetch({});
  }, [store.auth.session]);

  const initData = (product: Product) => {
    form.setValues((values: any) => ({
      ...values,
      name: product.name,
      description: product.description,
      category_id: product.category_id,
      images: [],
    }));

    form.setValues((values: any) => ({
      ...values,
      images: [],
      ...pick(product, [
        "name",
        "description",
        "category_id",
        "unit_type_id",
        "quantity",
        "selling_price",
        "status",
        "purchase_price",
        "reorder_point",
        "minimum_stock_level",
        "maximum_stock_level",
        "valuation_method",
      ]),
    }));

    if (product.featured_image) {
      form.setValue("featured_image", {
        file: undefined,
        src: product.featured_image?.url,
        id: product.featured_image.id,
      });
    }

    setCurrentImages(
      product.images.map((item: Asset) => ({
        file: undefined,
        src: item.url,
        id: item.id,
      })) || []
    );
  };

  const submit = async () => {
    store.errors.reset();
    if (!product) {
      return;
    }
    const data = unPick(form.values, ["featured_image"]);
    data["featured_image_id"] = product.featured_image?.id;

    // If the featured changed.
    if (
      product.featured_image?.id != form.values.featured_image?.id &&
      form.values.featured_image
    ) {
      // If a new image is uploaded for the featured image. Then we same its index.
      if (form.values.featured_image.file) {
        for (let i = 0; i < form.values.images.length; i++) {
          if (form.values.images[i].id == form.values.featured_image.id)
            data["featured_image_index"] = i;
        }
      } else {
        // otherwise we submit the id of the image selected.
        data["featured_image_id"] = currentImages.find(
          (item) => item.id == form.values.featured_image.id
        )?.id;
      }
    }

    // Images removed are one that where present at the beginning so store inside product.images and that have been deleted
    data["images_removed_ids"] = product.images
      .filter(
        (image) => !currentImages?.map((item) => item.src).includes(image.url)
      )
      .map((image) => image.id);

    store.product
      .updateCurrent(createFormData(data))
      .then(() => {
        store.product.fetch({});
        store.display.show("edit_product_success");
      })
      .catch((error) => {
        console.log(error);
        store.errors.catch(error);
      })
      .finally(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
  };

  return (
    <div className="max-w-6xl mx-auto">
      <Alert
        autoClose={3000}
        message={t("Product edit with success")}
        name={"edit_product_success"}
      />

      <BackButton />

      <div className="my-4 flex justify-between">
        <h2 className=" text-xl font-semibold">{t("Edit product")}</h2>
        <Button
          className="px-6 py-2"
          onClick={submit}
          loading={store.product.loading("update")}
        >
          {t("Save changes")}
        </Button>
      </div>

      <div>{store.errors.render()}</div>

      {product && (
        <ProductForm
          mode="edit"
          form={form}
          currentImages={currentImages}
          setCurrentImages={setCurrentImages}
        />
      )}

      <Button
        className="mt-8 px-6 py-3"
        onClick={submit}
        loading={store.product.loading("update")}
      >
        {t("Save changes")}
      </Button>
    </div>
  );
}
